import { Experiment, ExperimentClient } from "@amplitude/experiment-js-client"
import { useSagaAuth } from "@einride/saga-core"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

export interface AmplitudeExperimentContextValue {
  loading: boolean
  isFlagActive: (flagKey: string, variantValue?: string) => boolean
}

export const AmplitudeExperimentContext = createContext<
  AmplitudeExperimentContextValue | undefined
>(undefined)

const EXPERIMENT_DEPLOYMENT_KEY = import.meta.env.VITE_AMPLITUDE_EXPERIMENT_KEY

interface AmplitudeExperimentProviderProps {
  children: ReactNode
}

export const AmplitudeExperimentProvider = ({
  children,
}: AmplitudeExperimentProviderProps): React.JSX.Element => {
  const [loading, setLoading] = useState(false)
  const { mode } = useSagaAuth()

  const fetchVariants = async (client: ExperimentClient): Promise<void> => {
    setLoading(true)
    await client.fetch()
    setLoading(false)
  }

  const experimentClient = useMemo(() => {
    if (!EXPERIMENT_DEPLOYMENT_KEY) {
      return undefined
    }
    const client = Experiment.initializeWithAmplitudeAnalytics(EXPERIMENT_DEPLOYMENT_KEY, {
      // fallback to default amplitude host if env variable not set
      serverUrl:
        import.meta.env.VITE_AMPLITUDE_EXPERIMENT_URL || "https://api.lab.eu.amplitude.com",
    })
    return client
  }, [])

  useEffect(() => {
    if (experimentClient) {
      fetchVariants(experimentClient)
    }
  }, [experimentClient])

  const isFlagActive = useCallback(
    (flagKey: string, variantValue?: string): boolean => {
      // if in local development and SDK is not initialized, show all features.
      // this allows you to ignore feature flagging and enable all features during local development
      // but also test flags locally by setting the VITE_AMPLITUDE_EXPERIMENT_KEY environment variable
      if (mode === "development" && !experimentClient) {
        return true
      }
      if (!experimentClient) {
        return false
      }
      const variantObject = experimentClient.variant(flagKey)
      if (variantValue) {
        return variantObject.value === variantValue
      }

      return variantObject.value === "on"
    },
    [experimentClient, mode],
  )
  const value: AmplitudeExperimentContextValue = useMemo(
    () => ({
      isFlagActive,
      loading,
    }),
    [isFlagActive, loading],
  )
  return (
    <AmplitudeExperimentContext.Provider value={value}>
      {children}
    </AmplitudeExperimentContext.Provider>
  )
}

export const useExperiment = (): AmplitudeExperimentContextValue => {
  const context = useContext(AmplitudeExperimentContext)
  if (!context) {
    throw new Error("cannot use useExperiment without AmplitudeExperimentProvider")
  }
  return context
}
